import React, { useMemo, useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  Link as MuiLink,
  Box,
  Tooltip,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { Add, Remove, Launch, Info as InfoIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { getCompanySearchLink } from '../../utils/companySearch';
import { getGradeChipSx } from '../../utils/gradeColors';
import { formatCompanySizeLabel } from '../../utils/companySize';
import { getIndustryAbbreviation } from '../../utils/sicCodes';
import { formatTablePaginationLabel } from '../../utils/pagination';
import { mapRankedCompanies } from '../../utils/marketOverviewMappers';
import { RankedCompanyApi } from '../../types/marketOverview';

export type SizeBucketMeta = {
  id: string;
  label: string;
  count: number;
  order: number;
};

const stringToColor = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += (`00${value.toString(16)}`).slice(-2);
  }
  return color;
};

const getContrastText = (hexColor: string): string => {
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? '#111827' : '#ffffff';
};

const SizeCategoryChip: React.FC<{
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
}> = ({ label, count, selected, onClick }) => {
  const shouldCompactCount = count >= 1000;
  const countLabel = shouldCompactCount ? `${Math.floor(count / 1000)}K` : count.toLocaleString();
  const fullCountLabel = `${count.toLocaleString()} companies`;
  const badge = (
    <Box
      component="span"
      sx={{
        width: 30,
        height: 30,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: selected ? '#303EB3' : '#e5e7eb',
        color: selected ? '#ffffff' : '#374151',
        fontWeight: 700,
        fontSize: '0.68rem',
        lineHeight: 1,
      }}
    >
      {countLabel}
    </Box>
  );

  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.75,
        borderRadius: 999,
        border: selected ? '1px solid #303EB3' : '1px solid #d1d5db',
        background: selected ? '#303EB312' : '#f9fafb',
        color: selected ? '#303EB3' : '#6b7280',
        cursor: 'pointer',
        opacity: 1,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: '#303EB3',
          background: selected ? '#303EB320' : '#f3f4f6',
        },
      }}
    >
      <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.875rem', color: 'inherit' }}>
        {label}
      </Typography>
      {shouldCompactCount ? (
        <Tooltip title={fullCountLabel} arrow>
          {badge}
        </Tooltip>
      ) : (
        badge
      )}
    </Box>
  );
};

const SizeAnalysis: React.FC<{
  rankedCompanies: RankedCompanyApi[];
  totalCount: number;
  loading: boolean;
  sizeBuckets: SizeBucketMeta[];
  selectedSizeBucketId: string;
  onSelectSizeBucket: (id: string) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (page: number) => void;
}> = ({
  rankedCompanies,
  totalCount,
  loading,
  sizeBuckets,
  selectedSizeBucketId,
  onSelectSizeBucket,
  page,
  rowsPerPage,
  onPageChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const rankedCompaniesView = useMemo(() => mapRankedCompanies(rankedCompanies), [rankedCompanies]);

  const selectedBucket = useMemo(
    () => sizeBuckets.find((bucket) => bucket.id === selectedSizeBucketId) || null,
    [sizeBuckets, selectedSizeBucketId]
  );

  useEffect(() => {
    setSearchTerm('');
  }, [selectedSizeBucketId, page]);

  const filteredCompanies = useMemo(
    () =>
      rankedCompaniesView.filter((company) => {
        const companyName = company.cleanCompanyName.toLowerCase();
        return companyName.includes(searchTerm.trim().toLowerCase());
      }),
    [rankedCompaniesView, searchTerm]
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select an employee-size bucket to load ranked companies for that segment.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, pb: 1 }}>
          {sizeBuckets.map((bucket) => {
            if (bucket.id === 'unknown' && bucket.count === 0) {
              return null;
            }
            const isSelected = bucket.id === selectedSizeBucketId;
            return (
              <SizeCategoryChip
                key={bucket.id}
                label={formatCompanySizeLabel(bucket.label)}
                count={bucket.count}
                selected={isSelected}
                onClick={() => onSelectSizeBucket(bucket.id)}
              />
            );
          })}
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" sx={{ mb: 0 }}>
            {selectedBucket
              ? `Companies in Size Range: ${formatCompanySizeLabel(selectedBucket.label)}${
                  selectedBucket.id === 'unknown' || selectedBucket.id === 'not_provided'
                    ? ''
                    : ' employees'
                }`
              : 'Companies'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <input
              aria-label="Search Company"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search Company"
              style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid #d1d5db' }}
            />
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table stickyHeader sx={{ tableLayout: 'fixed', minWidth: 980 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: '6%' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '42%' }}>Company Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '16%' }}>Industry</TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '9%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Grade
                        <Tooltip title="Letter grade (A-F) assigned based on Sophare's scoring algorithm." arrow>
                          <InfoIcon fontSize="small" color="action" sx={{ ml: 0.5, fontSize: 16, cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '12%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Mean Pay Gap
                        <Tooltip title="Mean hourly pay gap percentage. Positive values indicate men are paid more." arrow>
                          <InfoIcon fontSize="small" color="action" sx={{ ml: 0.5, fontSize: 16, cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold', width: '7%' }}>Link</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCompanies.map((row, index) => (
                    <TableRow key={row.id || index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 'bold',
                            fontSize: row.cleanCompanyName.length > 45 ? '0.78rem' : '0.875rem',
                            lineHeight: 1.2,
                          }}
                        >
                          <MuiLink
                            component={RouterLink}
                            to={getCompanySearchLink(row.cleanCompanyName, row.id)}
                            underline="hover"
                            color="inherit"
                            sx={{ fontWeight: 'bold' }}
                          >
                            {row.cleanCompanyName}
                          </MuiLink>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">{row.latestYear}</Typography>
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const industryAbbreviation = getIndustryAbbreviation(row.finalIndustryCode);
                          const industryColor = stringToColor(industryAbbreviation);
                          return (
                            <Chip
                              label={industryAbbreviation}
                              size="small"
                              sx={{
                                backgroundColor: industryColor,
                                color: getContrastText(industryColor),
                                fontSize: '0.65rem',
                                fontWeight: 700,
                                height: 20,
                                maxWidth: '100%',
                                '& .MuiChip-label': {
                                  px: 1,
                                },
                              }}
                            />
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Chip label={row.letterGrade} size="small" sx={getGradeChipSx(row.letterGrade)} />
                      </TableCell>
                      <TableCell>
                        {row.diffMeanHourlyPercent !== null ? (
                          (() => {
                            const gapValue = row.diffMeanHourlyPercent;
                            const isPositive = gapValue > 0;
                            const isNegative = gapValue < 0;
                            return (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  color: isPositive ? 'error.main' : isNegative ? 'success.main' : 'text.secondary',
                                }}
                              >
                                {isPositive && <Add fontSize="small" />}
                                {isNegative && <Remove fontSize="small" />}
                                {Math.abs(gapValue).toFixed(1)}%
                              </Box>
                            );
                          })()
                        ) : (
                          <Typography variant="caption" color="text.disabled">N/A</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.companyLinkToGPGInfo ? (
                          <IconButton
                            href={row.companyLinkToGPGInfo}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            sx={{ color: 'primary.main' }}
                          >
                            <Launch fontSize="small" />
                          </IconButton>
                        ) : (
                          <Typography variant="caption" color="text.disabled">N/A</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(_, newPage) => onPageChange(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[rowsPerPage]}
              labelDisplayedRows={formatTablePaginationLabel}
            />
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default SizeAnalysis;
